import { ChevronDown, ChevronUp, Sparkles } from "lucide-react"
import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react"
import { GenerateWithModelLabel } from "@/components/media/generate-with-model-label"
import { AutosizeTextarea } from "@/components/ui/autosize-textarea"
import {
  clampFloatingCardPos,
  FloatingDraggableCard,
  floatingCardParent,
  snapshotFloatingCardPos,
  type FloatingCardPos,
  type FloatingDraggableCardHandle,
} from "@/components/ui/floating-draggable-card"
import { Textarea } from "@/components/ui/primitives"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"

const FLYOUT_WIDTH_REM = 32
const EDGE_PAD_PX = 8
const BOTTOM_PAD_PX = 12
const CARD_MIN_WIDTH = 320
const CARD_MAX_WIDTH_REM = FLYOUT_WIDTH_REM
const CARD_MAX_WIDTH_GUTTER_REM = 1.5
const CARD_MAX_HEIGHT_RATIO = 0.5
const FIELD_MIN_HEIGHT = 40
const FIELD_MAX_ROWS = 8

const FLOATING_CARD_CLASS =
  "z-20 flex w-80 max-w-[min(32rem,calc(100%-1.5rem))] flex-col gap-1.5 overflow-visible rounded-lg border border-ui-border p-3 shadow-[0_-8px_40px_-8px_rgba(0,0,0,0.45)] ring-1 ring-black/5 backdrop-blur-md dark:bg-zinc-950/80 dark:shadow-2xl dark:shadow-black/40 light:bg-ui-bg-elevated/88 light:shadow-[0_-4px_24px_rgba(0,0,0,0.12)]"

type CardLayout = { width: number; minFieldHeight: number }

const GRIP_VIEWBOX = 16
const GRIP_DOT_RADIUS = 1
const GRIP_DOT_GAP = 2
const GRIP_DOT_STEP = GRIP_DOT_GAP + GRIP_DOT_RADIUS * 2
const GRIP_EDGE_INSET = GRIP_DOT_GAP
const GRIP_CORNER_CX = GRIP_VIEWBOX - GRIP_EDGE_INSET - GRIP_DOT_RADIUS
const GRIP_CORNER_CY = GRIP_EDGE_INSET + GRIP_DOT_RADIUS

const SE_GRIP_DOTS = [
  { cx: GRIP_CORNER_CX, cy: GRIP_CORNER_CY },
  { cx: GRIP_CORNER_CX, cy: GRIP_CORNER_CY + GRIP_DOT_STEP },
  { cx: GRIP_CORNER_CX, cy: GRIP_CORNER_CY + GRIP_DOT_STEP * 2 },
  { cx: GRIP_CORNER_CX - GRIP_DOT_STEP, cy: GRIP_CORNER_CY },
  { cx: GRIP_CORNER_CX - GRIP_DOT_STEP, cy: GRIP_CORNER_CY + GRIP_DOT_STEP },
  { cx: GRIP_CORNER_CX - GRIP_DOT_STEP * 2, cy: GRIP_CORNER_CY },
]

function remToPx(rem: number): number {
  return rem * (Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16)
}

function cardMaxWidthPx(parentW: number): number {
  return Math.min(remToPx(CARD_MAX_WIDTH_REM), Math.max(0, parentW - remToPx(CARD_MAX_WIDTH_GUTTER_REM)))
}

function chromeHeight(panel: HTMLElement, field: HTMLElement | null): number {
  const fieldH = field?.offsetHeight ?? FIELD_MIN_HEIGHT
  return Math.max(0, panel.offsetHeight - fieldH)
}

function maxFieldHeightPx(parentH: number, chrome: number, top: number): number {
  const half = parentH * CARD_MAX_HEIGHT_RATIO - chrome
  const remain = parentH - top - chrome
  return Math.max(FIELD_MIN_HEIGHT, Math.min(half, remain))
}

function applyCardResize(
  origLeft: number,
  origTop: number,
  origWidth: number,
  origFieldH: number,
  origChrome: number,
  dx: number,
  dy: number,
  parentW: number,
  parentH: number,
  minW: number,
  maxW: number,
  minField: number,
  maxField: number,
): { pos: FloatingCardPos; layout: CardLayout } {
  let width = origWidth + dx
  let minFieldHeight = origFieldH + dy
  let left = origLeft
  let top = origTop

  const widthCap = Math.min(maxW, parentW)
  width = Math.min(Math.max(width, minW), widthCap)
  if (left + width > parentW) left = Math.max(0, parentW - width)

  minFieldHeight = Math.min(Math.max(minFieldHeight, minField), maxField)
  const cardH = origChrome + minFieldHeight
  if (top + cardH > parentH) {
    top = Math.max(0, parentH - cardH)
    minFieldHeight = Math.min(minFieldHeight, Math.max(minField, parentH - top - origChrome))
  }

  const pos = clampFloatingCardPos(left, top, parentW, parentH, width, origChrome + minFieldHeight)
  width = Math.min(Math.max(width, minW), Math.min(widthCap, Math.max(minW, parentW - pos.left)))
  minFieldHeight = Math.min(Math.max(minFieldHeight, minField), maxField)
  return {
    pos: clampFloatingCardPos(pos.left, pos.top, parentW, parentH, width, origChrome + minFieldHeight),
    layout: { width, minFieldHeight },
  }
}

export type SketchAiFormLabels = {
  heading: string
  placeholder: string
  apply: string
  applying: string
}

export type SketchAiFormProps = {
  prompt: string
  onPromptChange: (value: string) => void
  onApply: () => void
  busy: boolean
  disabled: boolean
  sketchAvailable: boolean
  modelLabel?: string
  disabledReason?: string
  quickPrompts: string[]
  labels: SketchAiFormLabels
  /** Stable SPA reconnect id for Apply. */
  confirmDelayKey: string
}

type Props = SketchAiFormProps & {
  open: boolean
  onClose: () => void
  anchorRef: React.RefObject<HTMLElement | null>
}

function dialogBoundsFor(anchor: HTMLElement | null): HTMLElement | null {
  const host = anchor?.closest('[data-workspace-dialog], [role="dialog"], [role="alertdialog"]') ?? null
  return host instanceof HTMLElement ? host : null
}

export function SketchAiFlyout({
  open,
  prompt,
  onPromptChange,
  onApply,
  busy,
  disabled,
  sketchAvailable,
  modelLabel,
  disabledReason,
  quickPrompts,
  labels,
  confirmDelayKey,
  onClose,
  anchorRef,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const chipsDisabled = busy || disabled || !sketchAvailable
  const [shiftX, setShiftX] = useState(0)
  const [maxWidthPx, setMaxWidthPx] = useState<number | null>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (panelRef.current?.contains(t)) return
      if (anchorRef.current?.contains(t)) return
      onClose()
    }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open, onClose, anchorRef])

  useEffect(() => {
    if (!open || busy || disabled || !sketchAvailable) return
    const el = textareaRef.current
    if (!el) return
    // Don't steal focus when prompt already has text (e.g. reopen flyout).
    if (el.value.trim()) return
    el.focus()
    el.select()
  }, [open, busy, disabled, sketchAvailable])

  useLayoutEffect(() => {
    if (!open) {
      setShiftX(0)
      setMaxWidthPx(null)
      return
    }

    const clamp = () => {
      const panel = panelRef.current
      const anchor = anchorRef.current
      if (!panel || !anchor) return

      const dialog = dialogBoundsFor(anchor)
      const bounds = dialog?.getBoundingClientRect()
      if (!bounds) {
        setShiftX(0)
        setMaxWidthPx(null)
        return
      }

      const available = Math.max(0, bounds.width - EDGE_PAD_PX * 2)
      const remPx = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      const preferred = FLYOUT_WIDTH_REM * remPx
      const width = Math.min(preferred, available)
      setMaxWidthPx(width)

      const parent = panel.offsetParent instanceof HTMLElement ? panel.offsetParent : panel.parentElement
      if (!parent) {
        setShiftX(0)
        return
      }

      const parentRect = parent.getBoundingClientRect()
      const desiredLeft = parentRect.left + parentRect.width / 2 - width / 2
      const minLeft = bounds.left + EDGE_PAD_PX
      const maxLeft = bounds.right - EDGE_PAD_PX - width
      const clampedLeft = Math.min(Math.max(desiredLeft, minLeft), maxLeft)
      setShiftX(clampedLeft - desiredLeft)
    }

    clamp()
    window.addEventListener("resize", clamp)
    const dialog = dialogBoundsFor(anchorRef.current)
    dialog?.addEventListener("scroll", clamp, true)
    return () => {
      window.removeEventListener("resize", clamp)
      dialog?.removeEventListener("scroll", clamp, true)
    }
  }, [open, anchorRef])

  if (!open) return null

  return (
    <div
      ref={panelRef}
      className="absolute top-full left-1/2 z-50 mt-1 flex w-[32rem] flex-col gap-2 rounded-lg border border-ui-border bg-ui-bg-elevated p-3 shadow-xl"
      style={{
        maxWidth: maxWidthPx != null ? maxWidthPx : `${FLYOUT_WIDTH_REM}rem`,
        transform: `translateX(calc(-50% + ${shiftX}px))`,
      }}
    >
      <p className="text-xs font-medium text-ui-text-secondary">{labels.heading}</p>
      {quickPrompts.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {quickPrompts.map((phrase) => (
            <button
              key={phrase}
              type="button"
              disabled={chipsDisabled}
              className={cn(
                "shrink-0 rounded-lg border border-ui-border bg-ui-surface px-2 py-1 text-[11px] whitespace-nowrap text-ui-text-secondary transition-colors hover:text-ui-text-primary",
                chipsDisabled && "pointer-events-none opacity-50",
              )}
              onClick={() => {
                onPromptChange(phrase)
                textareaRef.current?.focus()
              }}
            >
              {phrase}
            </button>
          ))}
        </div>
      ) : null}
      <Textarea
        ref={textareaRef}
        data-sketch-ai-prompt=""
        value={prompt}
        onChange={(e) => onPromptChange(e.target.value)}
        placeholder={labels.placeholder}
        rows={7}
        disabled={chipsDisabled}
        className="min-h-[11rem] text-sm"
      />
      <GenerateWithModelLabel
        label={labels.apply}
        busyLabel={labels.applying}
        modelLabel={modelLabel}
        busy={busy}
        disabled={!sketchAvailable || !prompt.trim() || disabled}
        disabledReason={disabledReason}
        onClick={onApply}
        confirmDelayKey={confirmDelayKey}
        className="items-stretch"
        buttonClassName="w-full"
      />
    </div>
  )
}

export type SketchAiFloatingCardProps = SketchAiFormProps & {
  /** Recenter until user drags; bump when dialog opens or source changes. */
  resetKey?: string | number
}

export function SketchAiFloatingCard({
  prompt,
  onPromptChange,
  onApply,
  busy,
  disabled,
  sketchAvailable,
  disabledReason,
  quickPrompts,
  labels,
  confirmDelayKey,
  resetKey = 0,
}: SketchAiFloatingCardProps) {
  const { t } = useTranslation("workspace")
  const cardRef = useRef<FloatingDraggableCardHandle | null>(null)
  const promptWrapRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const layoutRef = useRef<CardLayout | null>(null)
  const maxFieldRef = useRef(FIELD_MIN_HEIGHT)
  const resizeRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    origLeft: number
    origTop: number
    origWidth: number
    origFieldH: number
    origChrome: number
  } | null>(null)
  const [layout, setLayout] = useState<CardLayout | null>(null)
  const [maxFieldPx, setMaxFieldPx] = useState<number | undefined>(undefined)
  const [resizing, setResizing] = useState(false)
  const [chipsOpen, setChipsOpen] = useState(true)
  const chipsDisabled = busy || disabled || !sketchAvailable
  const showChipToggle = quickPrompts.length > 0
  const lockedFieldH = layout == null ? undefined : Math.min(layout.minFieldHeight, maxFieldPx ?? layout.minFieldHeight)
  layoutRef.current = layout

  useLayoutEffect(() => {
    setLayout(null)
    layoutRef.current = null
    setResizing(false)
    resizeRef.current = null
    document.body.style.removeProperty("cursor")
    document.body.style.removeProperty("user-select")
  }, [resetKey])

  useEffect(() => {
    return () => {
      document.body.style.removeProperty("cursor")
      document.body.style.removeProperty("user-select")
    }
  }, [])

  useLayoutEffect(() => {
    const panel = cardRef.current?.panel() ?? null
    const parent = floatingCardParent(panel)
    if (!panel || !parent) return

    const syncLayout = () => {
      const parentW = parent.clientWidth
      const parentH = parent.clientHeight
      const chrome = chromeHeight(panel, promptWrapRef.current)
      const top = cardRef.current?.getPos()?.top ?? parentH - BOTTOM_PAD_PX - panel.offsetHeight
      const nextMaxField = maxFieldHeightPx(parentH, chrome, Math.max(0, top))
      maxFieldRef.current = nextMaxField
      setMaxFieldPx((prev) => (prev === nextMaxField ? prev : nextMaxField))

      const maxW = cardMaxWidthPx(parentW)
      const prevLayout = layoutRef.current
      if (prevLayout) {
        const width = Math.min(Math.max(CARD_MIN_WIDTH, prevLayout.width), maxW)
        const minFieldHeight = Math.min(Math.max(FIELD_MIN_HEIGHT, prevLayout.minFieldHeight), nextMaxField)
        if (width !== prevLayout.width || minFieldHeight !== prevLayout.minFieldHeight) {
          const nextLayout = { width, minFieldHeight }
          layoutRef.current = nextLayout
          setLayout(nextLayout)
        }
      }

      const pos = cardRef.current?.getPos()
      if (!pos) return
      const cardW = layoutRef.current?.width ?? panel.offsetWidth
      const cardH = panel.offsetHeight
      if (cardW <= 0) return
      const next = clampFloatingCardPos(pos.left, pos.top, parentW, parentH, cardW, cardH)
      if (next.left !== pos.left || next.top !== pos.top) cardRef.current?.setPos(next)
    }

    syncLayout()
    const ro = new ResizeObserver(syncLayout)
    ro.observe(parent)
    return () => ro.disconnect()
  }, [resetKey, chipsOpen])

  useEffect(() => {
    if (!chipsOpen) return
    const onPointerDown = (event: PointerEvent) => {
      const root = cardRef.current?.panel()
      const target = event.target
      if (!(target instanceof Node) || !root) return
      if (root.contains(target)) return
      setChipsOpen(false)
      textareaRef.current?.blur()
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    return () => document.removeEventListener("pointerdown", onPointerDown, true)
  }, [chipsOpen])

  const onResizePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    const panel = cardRef.current?.panel() ?? null
    const parent = floatingCardParent(panel)
    if (!panel || !parent) return

    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)
    setResizing(true)
    document.body.style.cursor = "nwse-resize"
    document.body.style.userSelect = "none"

    const snapped = snapshotFloatingCardPos(panel, parent)
    const origWidth = panel.offsetWidth
    const origFieldH = textareaRef.current?.offsetHeight ?? promptWrapRef.current?.offsetHeight ?? FIELD_MIN_HEIGHT
    const origChrome = chromeHeight(panel, promptWrapRef.current)
    maxFieldRef.current = maxFieldHeightPx(parent.clientHeight, origChrome, snapped.top)
    cardRef.current?.setPos(
      clampFloatingCardPos(
        snapped.left,
        snapped.top,
        parent.clientWidth,
        parent.clientHeight,
        origWidth,
        panel.offsetHeight,
      ),
    )
    const nextLayout = {
      width: origWidth,
      minFieldHeight: origFieldH,
    }
    layoutRef.current = nextLayout
    setLayout(nextLayout)
    resizeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origLeft: snapped.left,
      origTop: snapped.top,
      origWidth,
      origFieldH,
      origChrome,
    }
  }

  const onResizePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const resize = resizeRef.current
    const panel = cardRef.current?.panel() ?? null
    const parent = floatingCardParent(panel)
    if (!resize || resize.pointerId !== event.pointerId || !panel || !parent) return
    const maxW = cardMaxWidthPx(parent.clientWidth)
    const next = applyCardResize(
      resize.origLeft,
      resize.origTop,
      resize.origWidth,
      resize.origFieldH,
      resize.origChrome,
      event.clientX - resize.startX,
      event.clientY - resize.startY,
      parent.clientWidth,
      parent.clientHeight,
      CARD_MIN_WIDTH,
      maxW,
      FIELD_MIN_HEIGHT,
      maxFieldRef.current,
    )
    layoutRef.current = next.layout
    setLayout(next.layout)
    cardRef.current?.setPos(next.pos)
  }

  const endResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    const resize = resizeRef.current
    if (!resize || resize.pointerId !== event.pointerId) return
    resizeRef.current = null
    setResizing(false)
    document.body.style.removeProperty("cursor")
    document.body.style.removeProperty("user-select")
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  return (
    <FloatingDraggableCard
      cardRef={cardRef}
      resetKey={resetKey}
      className={FLOATING_CARD_CLASS}
      aria-label={labels.heading}
      unsetStyle={{ left: "50%", bottom: BOTTOM_PAD_PX, top: "auto", transform: "translateX(-50%)" }}
      style={layout ? { width: layout.width, maxWidth: "none" } : undefined}
      header={
        <>
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-ui-accent" />
          <span className="min-w-0 truncate text-xs font-medium text-ui-text-primary">{labels.heading}</span>
        </>
      }
      headerEnd={
        showChipToggle ? (
          <button
            type="button"
            className="ml-auto shrink-0 text-ui-text-secondary hover:text-ui-text-primary"
            aria-expanded={chipsOpen}
            aria-label={chipsOpen ? t("agent.collapse") : t("agent.expand")}
            onClick={() => setChipsOpen((v) => !v)}
          >
            {chipsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
          </button>
        ) : null
      }
    >
      {showChipToggle ? (
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-120 ease-linear",
            chipsOpen ? "mt-1.5 grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
          aria-hidden={!chipsOpen}
          {...(!chipsOpen ? { inert: true } : {})}
        >
          <div
            className={cn(
              "min-h-0 overflow-hidden transition-opacity duration-120 ease-linear",
              chipsOpen ? "opacity-100" : "opacity-0",
            )}
          >
            <div className="flex flex-wrap gap-1">
              {quickPrompts.map((phrase) => (
                <button
                  key={phrase}
                  type="button"
                  disabled={chipsDisabled}
                  className={cn(
                    "shrink-0 rounded-md border border-ui-border bg-ui-surface px-1.5 py-0.5 text-[11px] whitespace-nowrap text-ui-text-secondary transition-colors hover:text-ui-text-primary",
                    chipsDisabled && "pointer-events-none opacity-50",
                  )}
                  onClick={() => {
                    onPromptChange(phrase)
                    textareaRef.current?.focus()
                  }}
                >
                  {phrase}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
      <div
        ref={promptWrapRef}
        className="relative min-w-0 shrink-0 overflow-hidden rounded-lg border border-ui-border bg-ui-surface-sunken"
      >
        <AutosizeTextarea
          ref={textareaRef}
          data-sketch-ai-prompt=""
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder={labels.placeholder}
          disabled={chipsDisabled}
          minRows={1}
          maxRows={FIELD_MAX_ROWS}
          minHeightPx={lockedFieldH}
          maxHeightPx={lockedFieldH ?? maxFieldPx}
          className="min-h-10 w-full min-w-0 border-0 bg-transparent pr-12 text-sm shadow-none focus-visible:ring-0"
          onFocus={() => setChipsOpen(true)}
          onPointerDown={() => setChipsOpen(true)}
        />
        <GenerateWithModelLabel
          label={labels.apply}
          busyLabel={labels.applying}
          iconOnly
          busy={busy}
          disabled={!sketchAvailable || !prompt.trim() || disabled}
          disabledReason={disabledReason}
          onClick={onApply}
          confirmDelayKey={confirmDelayKey}
          labelClassName="hidden"
          className="absolute right-1 bottom-1"
        />
      </div>
      <div
        role="separator"
        aria-label={t("canvas.sketch.resizePanelAria")}
        aria-orientation="horizontal"
        className={cn(
          "absolute right-1 bottom-1 z-10 flex h-4 w-4 cursor-nwse-resize touch-none items-center justify-center text-ui-text-muted",
          resizing && "text-ui-text-secondary",
        )}
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={endResize}
        onPointerCancel={endResize}
      >
        <svg aria-hidden viewBox={`0 0 ${GRIP_VIEWBOX} ${GRIP_VIEWBOX}`} className="pointer-events-none h-full w-full">
          <g transform="scale(1 -1) translate(0 -16)" fill="currentColor">
            {SE_GRIP_DOTS.map((dot, index) => (
              <circle key={index} cx={dot.cx} cy={dot.cy} r={GRIP_DOT_RADIUS} />
            ))}
          </g>
        </svg>
      </div>
    </FloatingDraggableCard>
  )
}
